import React from "react";
import { Link } from "react-router-dom";
import { Youtube, Instagram, Facebook, ShoppingBag, Coffee, ExternalLink as ExternalLinkIcon, Tent, ChevronRight } from "lucide-react";
import ExternalLink from "../components/ExternalLink";
import SuggestionForm from "../components/SuggestionForm";

const PROFILE = {
  name: "OJ Adventures",
  handle: "@oj_adventures",
  bio:
    "🐶 Jason & Ozzy the Border Collie sharing hikes, camps and outdoor adventures across South Wales. " +
    "Catch our latest YouTube videos, grab OJ Adventures merch — and join the trail.",
  avatar:
    "https://ugc.production.linktr.ee/08f10d4b-3d44-4f8e-abf1-4b329a5c3f84_4a26df57-30f9-4eeb-918e-ab821b29cdb5-1-all-10444.jpeg?io=true&size=avatar-v3_0",
};

/* ---------- brand SVGs (lucide doesn't ship TikTok / Threads / X) ---------- */
function TikTokIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M21 8.5a7.4 7.4 0 0 1-4.3-1.4v8.6a6.6 6.6 0 1 1-6.6-6.6c.5 0 1 .1 1.4.2v3.4a3.2 3.2 0 1 0 2.2 3V2h3.3a4.4 4.4 0 0 0 4 4.5z" />
    </svg>
  );
}
function ThreadsIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12.2 22h-.1c-3 0-5.3-1-6.8-3-1.4-1.7-2.1-4.2-2.1-7.1 0-3 .7-5.4 2.1-7.1C6.8 3 9.1 2 12 2c2.6 0 4.6.7 6.1 2.1 1.3 1.3 2.1 3 2.4 5l-2.6.6c-.5-3-2.1-4.5-5.9-4.5-2.2 0-3.8.7-4.9 2-1 1.3-1.6 3.2-1.6 5.7 0 2.5.6 4.5 1.6 5.8 1.1 1.3 2.7 2 4.9 2 1.8 0 3-.4 3.9-1.2.7-.6 1.1-1.4 1.1-2.5 0-1.7-1.5-2.6-3.4-2.6-1.7 0-3 .8-3 2 0 .9.8 1.4 1.7 1.4 1 0 1.7-.4 2-1l2.5 1.1c-.8 2-2.6 2.5-4.5 2.5-2.8 0-5-1.6-5-4 0-2.7 2.4-4.7 6.1-4.7 3 0 6.3 1.4 6.3 5.2 0 4-3.5 5.1-6 5.1z" />
    </svg>
  );
}

// Compact social row — icons only, no descriptions.
const SOCIALS = [
  { label: "YouTube",   href: "https://www.youtube.com/@OJ_Adventures",     Icon: Youtube,      color: "#FF0033" },
  { label: "Instagram", href: "https://www.instagram.com/oj_adventure/",    Icon: Instagram,    color: "#E4405F" },
  { label: "TikTok",    href: "https://www.tiktok.com/@oj_adventure",       Icon: TikTokIcon,   color: "#000000" },
  { label: "Threads",   href: "https://www.threads.com/@oj_adventure",      Icon: ThreadsIcon,  color: "#000000" },
  { label: "Facebook",  href: "https://www.facebook.com/ojadventure",       Icon: Facebook,     color: "#1877F2" },
];

// Full-width CTA cards retained below the social row.
const CTA_LINKS = [
  {
    label: "Etsy Shop",
    href: "https://www.etsy.com/uk/shop/OjAdventures",
    Icon: ShoppingBag,
    color: "#F1641E",
    note: "OJ Adventures merch",
  },
  {
    label: "Buy me a coffee",
    href: "https://buymeacoffee.com/ojadventures",
    Icon: Coffee,
    color: "#FFDD00",
    iconText: "#000000",
    note: "Support new adventures",
  },
];

function SocialIcon({ link }) {
  const { label, href, Icon, color } = link;
  return (
    <ExternalLink
      href={href}
      testid={`about-social-${label.toLowerCase()}`}
      aria-label={label}
      title={label}
      className="
        shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white
        transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md active:scale-95
      "
      style={{ backgroundColor: color }}
    >
      <Icon size={20} />
    </ExternalLink>
  );
}

function LinkButton({ link }) {
  const { label, href, Icon, color, note, iconText } = link;
  return (
    <ExternalLink
      href={href}
      testid={`about-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="
        group flex items-center gap-3
        td-card px-4 py-3
        transition-all duration-200
        hover:-translate-y-0.5 hover:shadow-md hover:border-brand-primary/40
        active:scale-[0.99]
        cursor-pointer
      "
    >
      <span
        className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: color, color: iconText || "#FFFFFF" }}
      >
        <Icon size={20} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-heading text-base sm:text-lg text-brand-text leading-tight truncate">
          {label}
        </span>
        {note && (
          <span className="block text-xs text-brand-subtle truncate">{note}</span>
        )}
      </span>
      <ExternalLinkIcon
        size={15}
        className="text-brand-subtle group-hover:text-brand-primary transition-colors shrink-0"
      />
    </ExternalLink>
  );
}

export default function About() {
  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-8 pb-48 sm:pb-40" data-testid="about-page">
      {/* Avatar + heading */}
      <div className="flex flex-col items-center text-center">
        <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-brand-surface shadow-md bg-brand-border/40">
          <img
            src={PROFILE.avatar}
            alt="OJ Adventures profile"
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="td-section-label mt-4">About</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text mt-1">
          {PROFILE.name}
        </h1>
        <p className="text-brand-subtle text-sm mt-0.5">{PROFILE.handle}</p>
        <p className="text-brand-text/90 text-[15px] leading-relaxed mt-4 max-w-md">
          {PROFILE.bio}
        </p>
      </div>

      {/* Compact social row */}
      <div
        className="mt-8 flex items-center justify-center gap-3 sm:gap-4"
        data-testid="about-socials"
        aria-label="Follow OJ Adventures on social media"
      >
        {SOCIALS.map((s) => (
          <SocialIcon key={s.label} link={s} />
        ))}
      </div>

      {/* Full-width CTA cards */}
      <div className="mt-6 space-y-3" data-testid="about-ctas">
        {CTA_LINKS.map((l) => (
          <LinkButton key={l.label} link={l} />
        ))}
        {/* Internal — Gear & Partners (uses SPA Link, not ExternalLink) */}
        <Link
          to="/gear"
          data-testid="about-link-gear"
          className="
            group flex items-center gap-3
            td-card px-4 py-3
            transition-all duration-200
            hover:-translate-y-0.5 hover:shadow-md hover:border-brand-primary/40
            active:scale-[0.99]
            cursor-pointer
          "
        >
          <span
            className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center text-white"
            style={{ backgroundColor: "#B16B3C" }}
          >
            <Tent size={20} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-heading text-base sm:text-lg text-brand-text leading-tight truncate">
              Gear &amp; Partners
            </span>
            <span className="block text-xs text-brand-subtle truncate">
              Kit we actually use on the trail
            </span>
          </span>
          <ChevronRight
            size={16}
            className="text-brand-subtle group-hover:text-brand-primary transition-colors shrink-0"
          />
        </Link>
      </div>

      {/* Feature suggestion drop-box */}
      <div className="mt-6" data-testid="about-suggest">
        <SuggestionForm />
      </div>

      <p className="text-center text-xs text-brand-subtle mt-8">
        TrailDog · Ozzy&apos;s Basecamp · made with paws &amp; coffee
      </p>
    </div>
  );
}
